import {
  Button,
  useToast,
  DrawerFooter,
  Box,
  DrawerBody,
  Flex,
  Text,
  Spacer,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'sql-formatter';

import { DataLake } from '@finley-technologies/finley-types';

import { AthenaTableGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { ManualSqlQueryHelperText } from '../CollateralDataQualityCheckComponents';
import { getQueryTestResultMissingHeaders } from '../utils';
import { AddCollateralDataQualityCheckModal } from './AddCollateralDataQualityCheckModal';
import { CollateralDataQualityCheckQueryResultsTable } from './CollateralDataQualityCheckQueryResultsTable';
import { useAthenaMonacoEditor } from '~/packages/athena/hooks/useAthenaMonacoEditor';
import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '~/packages/athena/requests/requests.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { lightTheme } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/common';

const sqlFormat: (queryString: string) => string = (queryString: string) =>
  format(queryString, {
    language: 'trino', // athena uses trino & presto
  });

const TestQueryResultMissingFields = ({
  queryMissingFields,
}: {
  queryMissingFields: string[] | undefined;
}) => {
  if (!queryMissingFields?.length) {
    return null;
  }

  return (
    <Alert status="error" mt="24px" borderRadius="8px">
      <AlertIcon alignSelf="flex-start" />
      <Box>
        <AlertTitle mb="16px">{`Field${
          queryMissingFields.length === 1 ? '' : 's'
        } missing: Ensure the field${
          queryMissingFields.length === 1 ? ' is' : 's are'
        } selected & named correctly in the query.`}</AlertTitle>
        <AlertDescription>
          <VStack spacing="8px" alignItems="left">
            {queryMissingFields.map((missingField) => (
              <Text color={colors.black[70]} key={missingField}>
                {missingField}
              </Text>
            ))}
          </VStack>
        </AlertDescription>
      </Box>
    </Alert>
  );
};

const QueryExecutionErrors = ({
  queryExecutionErrors,
}: {
  queryExecutionErrors: string;
}) => {
  return (
    <Alert status="error" mt="24px" borderRadius="8px">
      <AlertIcon alignSelf="flex-start" />
      <Box>
        <AlertTitle mb="16px">Query Preview Error</AlertTitle>
        <AlertDescription>{queryExecutionErrors}</AlertDescription>
      </Box>
    </Alert>
  );
};

type ManualQueryEditContainerProps = {
  organizationId: string;
  initialQuery: string;
  athenaTables: AthenaTableGQLType[];
  onConfirmAddCdq: ({
    name,
    query,
  }: {
    name: string;
    query: string;
  }) => Promise<void>;
  isAddLoading: boolean;
};
export const CollateralDataQualityCheckManualQueryEditContainer = ({
  organizationId,
  initialQuery,
  athenaTables,
  onConfirmAddCdq,
  isAddLoading,
}: ManualQueryEditContainerProps) => {
  useAthenaMonacoEditor({ athenaTables });

  const {
    isOpen: isSaveCdqModalOpen,
    onOpen: onSaveCdqModalOpen,
    onClose: onSaveCdqModalClose,
  } = useDisclosure();

  const [isShowHelpEnabled, setIsShowHelpEnabled] = useState<boolean>(false);

  const [query, setQuery] = useState<string>(initialQuery);
  const toast = useToast();
  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[] | undefined
  >();
  const [queryExecutionErrors, setQueryExecutionErrors] = useState<
    string | undefined
  >();
  const [queryMissingFields, setQueryMissingFields] = useState<
    string[] | undefined
  >();
  const [isQueryValid, setIsQueryValid] = useState(false);

  // update query to invalid every time it changes
  useEffect(() => {
    setIsQueryValid(false);
  }, [query]);

  const [
    getAthenaQueryResponseByRaw,
    { loading: isQueryLoading },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    onError: (err) => {
      setQueryExecutionErrors(err.message);
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.athenaQueryResponse.Items.length) {
        setQueryDataItems(data.athenaQueryResponse.Items);

        const missingFields = getQueryTestResultMissingHeaders({
          queryResults: data.athenaQueryResponse.Items,
        });
        setQueryMissingFields(missingFields);

        // if query results are returned and there are no missing fields
        // set query as valid and able to be saved
        if (!missingFields.length) {
          setIsQueryValid(true);
        }
      } else if (data.athenaQueryResponse.Items.length === 0) {
        setQueryExecutionErrors('This query did not return any results.');
      }
    },
  });

  const executeRawQuery = useCallback(async () => {
    // reset the any data and errors from previous query
    setQueryDataItems([]);
    setQueryExecutionErrors('');

    try {
      await getAthenaQueryResponseByRaw({
        variables: {
          organizationId,
          query,
          bindings: [],
          variables: [],
        },
      });
    } catch (err) {
      toast({
        ...errorToastConfig,
        title: 'Error when executing Athena query',
      });
    }
  }, [getAthenaQueryResponseByRaw, organizationId, query, toast]);

  const onConfirmAdd = useCallback(
    async (name: string) => {
      return onConfirmAddCdq({ name, query });
    },
    [onConfirmAddCdq, query],
  );

  return (
    <>
      {isSaveCdqModalOpen && (
        <AddCollateralDataQualityCheckModal
          isOpen={isSaveCdqModalOpen}
          onClose={onSaveCdqModalClose}
          onConfirmAdd={onConfirmAdd}
          isAddLoading={isAddLoading}
        />
      )}
      <DrawerBody>
        <Flex alignItems="center" mb="16px">
          <Text fontWeight={500}>SQL Query</Text>
          <Spacer />
          <Button
            variant="link"
            color={colors.info}
            onClick={() => setIsShowHelpEnabled((current) => !current)}
            textDecoration="none"
          >
            {isShowHelpEnabled ? 'Hide Help' : 'Show Help'}
          </Button>
        </Flex>
        {isShowHelpEnabled && <ManualSqlQueryHelperText />}

        {/* Query Input */}
        <Box
          resize="vertical"
          overflowY="visible"
          h="400px"
          minH="400px"
          width="100%"
          borderWidth="1px"
          borderColor={colors.gray[200]}
          borderRadius="8px"
          p="24px 24px 24px 0"
          backgroundColor="white"
          className="editor-container"
        >
          <Box h="320px" minH="320px" width="100%">
            <Editor
              language="sql"
              value={query}
              onChange={(val) => setQuery(val ?? '')}
              theme={lightTheme}
              options={{
                readOnly: false,
                lineNumbers: 'on',
                overviewRulerBorder: true,
                overviewRulerLanes: 3,
                renderLineHighlight: 'all',
                scrollbar: { vertical: 'auto' },
                minimap: { enabled: false },
                fixedOverflowWidgets: false,
                suggest: {
                  preview: true,
                  showInlineDetails: true,
                },
              }}
            />
          </Box>
          <Flex justifyContent="flex-end" py="16px">
            <Button
              onClick={() => setQuery(sqlFormat(query))}
              variant="ghost"
              p="0"
              h="20px"
            >
              <Icon icon="file-lines" boxSize={3} />
              <Text
                color={colors.black[70]}
                fontSize="12px"
                fontWeight={500}
                ml="8px"
              >
                Format SQL
              </Text>
            </Button>
          </Flex>
        </Box>

        {/* Execution and Validation Alerts */}
        {!!queryExecutionErrors && !isQueryLoading && (
          <QueryExecutionErrors queryExecutionErrors={queryExecutionErrors} />
        )}
        {!!queryDataItems && !isQueryLoading && !queryExecutionErrors && (
          <TestQueryResultMissingFields
            queryMissingFields={queryMissingFields}
          />
        )}

        {/* Query Results */}
        {!!queryDataItems?.length && !isQueryLoading && (
          <CollateralDataQualityCheckQueryResultsTable
            queryDataItems={queryDataItems}
          />
        )}
      </DrawerBody>
      <DrawerFooter>
        {isQueryValid ? (
          <Button
            color="white"
            backgroundColor={colors.aurora[400]}
            isDisabled={isAddLoading}
            isLoading={isAddLoading}
            onClick={onSaveCdqModalOpen}
            aria-label="save query"
          >
            Save Query
          </Button>
        ) : (
          <Button
            color="white"
            backgroundColor={colors.aurora[400]}
            isDisabled={isQueryLoading}
            isLoading={isQueryLoading}
            onClick={executeRawQuery}
            aria-label="test query"
          >
            Test Query
          </Button>
        )}
      </DrawerFooter>
    </>
  );
};
